import {
	Anchor,
	DateString,
	Icon,
	NoResults,
	Space,
	PipeSeparator
} from "client/Components";
import { DashboardCategoryViewModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import styles from "./CategoryPreviewCard.module.less";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { setTrackingId } from "client/Misc/utils";

const MAX_ITEM_NUMBER_ON_MOBILE = 2;

export type CategoryPreviewCardProps = {
	category: DashboardCategoryViewModel;
	onCategoryClick: (categoryId: number) => void;
	onNavigateToItem: (id: number) => void;
	timezone: TimeZone;
};

export const CategoryPreviewCard = (props: CategoryPreviewCardProps): ReactElement => {
	const {
		category,
		onCategoryClick,
		onNavigateToItem,
		timezone
	} = props;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const titleClickHandler = (articleId: number): void => {
		onNavigateToItem(articleId);
	};

	const maxItemNumber = useMemo(
		(): number => {
			const topItemsLength: number = category.topItems.length;
			if (topItemsLength <= MAX_ITEM_NUMBER_ON_MOBILE) {
				return topItemsLength;
			}

			return isMobile ? MAX_ITEM_NUMBER_ON_MOBILE : topItemsLength;
		},
		[category.topItems, isMobile]
	);

	const renderHeader = useCallback(
		(): ReactElement => (
			<h3
				{...setTrackingId("name")}
				className={styles.groupTitle}
				onClick={() => onCategoryClick(category.id)}
			>
				{category.name}
			</h3>
		),
		[category, onCategoryClick]
	);

	return (
		<div
			{...setTrackingId("category-card", `|${category.id}`)}
			className={styles.root}
		>
			{isMobile && renderHeader()}
			<div className={styles.card}>
				{!isMobile && renderHeader()}
				{category.topItems.length > 0 ? (
					<>
						<ul className={styles.articleList}>
							{category.topItems
								.slice(0, maxItemNumber)
								.map((item) => {
									return (
										<li
											{...setTrackingId("article", `|${item.id}`)}
											key={item.id}
											className={styles.articlePreview}
											onClick={() => titleClickHandler(item.id)}
										>
											{item.isNew && (
												<div
													{...setTrackingId("status-dot")}
													className={styles.statusDot}
												/>
											)}
											<div className={styles.thumbnail}>
												<HubThumbnail
													title={item.title}
													media={item.media}
													hubItemKind={HubItemKind.KnowledgeItem}
													mediaPlaceholderSize="small"
												/>
											</div>
											<h4
												{...setTrackingId("title")}
												className={styles.articleTitle}
											>
												{item.pinDate && (
													<span className={styles.pinIcon}>
														<Icon
															value={
																{
																	kind:"font",
																	name:"thumbtack",
																	color:"primary"
																}
															}
														/>
													</span>
												)}
												{item.title}
											</h4>
											<p className={styles.articleInfo}>
												<Icon value="pen" size="small" />
												<span>
													<DateString
														value={item.lastModifiedDate}
														format="RelativeTimestamp"
														timezone={timezone}
													/>
													{item.author?.name && (
														<>
															<PipeSeparator />
															{localize("_(Edited)By")}<Space />
															{item.author.name}
														</>
													)}
												</span>
											</p>
										</li>
									);
								})
							}
						</ul>
						<footer className={styles.footer}>
							<Anchor
								onClick={() => onCategoryClick(category.id)}
								className={styles.footerAnchor}
							>
								{localize("_ShowAllItems")} ({category.amountOfPublishedItems})
							</Anchor>
						</footer>
					</>
				) : (
					<div className={styles.noResult}>
						<NoResults
							icon="book"
							title={localize("_NoKnowledgeArticlesYet")}
							message={localize("_ThereAreNoPublishedItemsInThisCategoryYet")}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

CategoryPreviewCard.displayName = "CategoryPreviewCard";
